import React from "react"
import { Button } from "../button"
import { Icon } from "../icon"
import { cn } from "../utils"
import { scrollByPage } from "./overflow-scroll-utils"
import type { ScrollDirection } from "./overflow-scroll-utils"

// =============================================================================
// Styles
// =============================================================================

const overflowScrollButtonContainerStyles =
  "absolute inset-y-0 flex items-center"

// =============================================================================
// Component
// =============================================================================

export type OverflowScrollButtonsProps = {
  scrollerRef: React.RefObject<HTMLElement>
  hasLeftOverflow: boolean
  hasRightOverflow: boolean
  "data-slot": string
}

/**
 * Buttons at each overflowing edge that scroll the items by a page for pointer
 * users. Keyboard users reach every item with Tab or the arrow keys, so the
 * buttons are hidden from assistive tech and the tab order.
 *
 * Render beside the scroller, inside the element with
 * `overflowScrollContainerStyles`, with `scrollButtonsProps` from
 * `useOverflowScroll`.
 */
export function OverflowScrollButtons({
  scrollerRef,
  hasLeftOverflow,
  hasRightOverflow,
  "data-slot": dataSlot
}: OverflowScrollButtonsProps) {
  const renderScrollButton = (direction: ScrollDirection) => (
    <div
      className={cn(
        overflowScrollButtonContainerStyles,
        direction === "start" ? "left-0" : "right-0"
      )}
    >
      <Button
        aria-hidden
        tabIndex={-1}
        variant="tertiary"
        size="small"
        telemetry={false}
        data-slot={dataSlot}
        // Keep focus where it was rather than on a button hidden from
        // assistive tech, which unmounts once there's nothing left to scroll
        onMouseDown={event => event.preventDefault()}
        onClick={() =>
          scrollerRef.current && scrollByPage(scrollerRef.current, direction)
        }
      >
        <Icon name={direction === "start" ? "left" : "right"} size="xs" />
      </Button>
    </div>
  )

  return (
    <>
      {hasLeftOverflow && renderScrollButton("start")}
      {hasRightOverflow && renderScrollButton("end")}
    </>
  )
}
